import { useQuery } from "@tanstack/react-query";
import { useAuthStore, selectUser, canAccessSite, canAccessMenuSite, hasMenu } from "@safers/auth";
import { sitesService, REGION_DISPLAY_NAMES } from "@/services";
import {
  interpret,
  matchSites,
  siteWithSuffix,
  type CommandSite,
  type Interpretation,
} from "@/lib/command/resolve";
import { TARGETS, type TargetKind } from "@/lib/command/targets";

const SITES_STALE_MS = 5 * 60_000;

export function useCommandInterpreter(currentSiteId: number | null, enabled: boolean) {
  const user = useAuthStore(selectUser);

  const { data: sites = [], isPending } = useQuery({
    queryKey: ["sites", "command-palette"],
    queryFn: () => sitesService.getSites({ size: 1000 }),
    enabled,
    staleTime: SITES_STALE_MS,
    select: (res): CommandSite[] =>
      res.data.content.map((s) => ({
        id: s.id,
        name: s.name,
        region: REGION_DISPLAY_NAMES[s.region],
      })),
  });

  const canGo = (kind: TargetKind, siteId: number | null) => {
    const menu = TARGETS[kind].menu;
    if (siteId == null) return menu ? hasMenu(user, menu) : true;
    if (!canAccessSite(user, siteId)) return false;
    return menu ? canAccessMenuSite(user, siteId, menu) : true;
  };

  const run = (text: string): Interpretation => {
    if (isPending) {
      return { type: "unknown", say: "현장 목록을 불러오는 중이에요. 잠시 뒤 다시 말해 주세요." };
    }
    if (!sites.length) {
      return { type: "unknown", say: "현장 목록을 불러오지 못했어요. 잠시 뒤 다시 시도해 주세요." };
    }
    return interpret(text, sites, canGo, { currentSiteId });
  };

  const runForSite = (kind: TargetKind, site: CommandSite): Interpretation => {
    const path = TARGETS[kind].path(site.id);
    if (!path || !canGo(kind, site.id)) {
      return { type: "unknown", say: "그 현장은 열 수 없어요." };
    }
    return {
      type: "go",
      path,
      say:
        kind === "dashboard"
          ? `${siteWithSuffix(site.name)}으로 이동할게요.`
          : `${site.name} ${TARGETS[kind].label}로 이동할게요.`,
    };
  };

  const runBest = (candidates: string[]): { said: string; result: Interpretation } => {
    const texts = candidates.filter((t) => t.trim());
    for (const said of texts) {
      const result = run(said);
      if (result.type !== "unknown") return { said, result };
    }
    const said = texts[0] ?? "";
    return { said, result: run(said) };
  };

  const pickSite = (text: string, from: CommandSite[]): CommandSite | null =>
    matchSites(text, from)[0] ?? null;

  return { run, runBest, runForSite, pickSite };
}
